import { clamp } from "lodash-es";
import { GameEngine } from "~/engine/instance";
import { PlayerEntity } from "~/entities/player";
import { Vector2 } from "~/state/game-types";
import { isIteratorNonEmpty } from "~/utils/objects";

/**
 * Manages player movement
 */
export class MovementSystem {
  private static GRAVITY_Y = 600.0; // Units per second squared
  private static BASE_DRAG_COEFFICIENT_X = 6.0; // Base drag force coefficient (higher = more drag)
  private static BASE_DRAG_COEFFICIENT_Y = 2.0; // Base drag force coefficient (higher = more drag)

  constructor(private readonly player: PlayerEntity) {}

  // TODO: make this a player upgrade
  get autoStepUpHeight(): number {
    return GameEngine.voxelTerrain.voxelSize;
  }

  /**
   * Update called each frame
   */
  public update(deltaTime: number): void {
    this.applyForces(deltaTime);

    // Handle collision detection separately for X and Y axes
    this.handleMovementWithCollision(deltaTime);
  }

  private applyForces(deltaTime: number): void {
    const gameState = GameEngine.gameStore.getState();
    const movementSpeed = gameState.playerUpgrades.movementSpeed;
    const inputDirection = GameEngine.inputManager.wasdDirection;

    const maxVelocityX = movementSpeed;
    const maxVelocityY = 400;

    const player = this.player;

    const width2 = player.boxSize.width * 0.5;
    const height2 = player.boxSize.height * 0.5;
    const playerRect = {
      min: { x: player.position.x - width2, y: player.position.y - height2 },
      max: { x: player.position.x + width2, y: player.position.y + height2 },
    };

    // Get all voxels the player overlaps with,
    // and calculate average movement drag from all overlapping voxels
    let totalDrag = 0;
    for (const { terrainInfo } of GameEngine.voxelTerrain.getVoxelsInRect(
      playerRect.min,
      playerRect.max,
      false, // withSolids=false means only include non-solid voxels
    )) {
      if (terrainInfo.movementDrag != null) {
        totalDrag += terrainInfo.movementDrag;
      }
    }
    const movementDrag = totalDrag;

    const velocity = player.velocity;

    // Apply movement input to velocity (acceleration-based movement)
    if (inputDirection.x !== 0) {
      velocity.x += inputDirection.x * movementSpeed * deltaTime;
    }

    if (inputDirection.y > 0) {
      // flight
      velocity.y += inputDirection.y * movementSpeed * deltaTime;
    } else {
      // Apply gravity to downward velocity
      velocity.y -= MovementSystem.GRAVITY_Y * deltaTime;
    }

    // Apply drag to both axes, taking into account terrain movementDrag
    const dragX =
      velocity.x *
      (MovementSystem.BASE_DRAG_COEFFICIENT_X + movementDrag) *
      deltaTime;
    const dragY =
      velocity.y *
      (MovementSystem.BASE_DRAG_COEFFICIENT_Y + movementDrag) *
      deltaTime;

    velocity.x -= dragX;
    velocity.y -= dragY;

    // Clamp velocity to maximum limits
    velocity.x = clamp(velocity.x, -maxVelocityX, maxVelocityX);
    velocity.y = clamp(velocity.y, -maxVelocityY, maxVelocityY);
  }

  /**
   * Handle movement with proper collision detection for X and Y axes separately
   */
  private handleMovementWithCollision(deltaTime: number): void {
    const player = this.player;

    const velocity = player.velocity;

    let playerX = player.position.x;
    let playerY = player.position.y;

    // Try X movement first
    if (velocity.x !== 0) {
      const deltaX = velocity.x * deltaTime;
      const nextX = playerX + deltaX;

      const isCollidingX = this.doesCollide(nextX, playerY);

      if (!isCollidingX) {
        playerX = nextX;
      } else {
        // Try step-up before binary search
        const stepUpResultY = this.tryStepUp(playerX, playerY, nextX);

        if (stepUpResultY != null) {
          playerX = nextX;
          playerY = stepUpResultY;
        } else {
          // Find the closest valid position by binary search
          playerX = this.findClosestValidPosition(playerX, nextX, playerY, "x");
          velocity.x = 0;
        }
      }
    }

    // Try Y movement second
    if (velocity.y !== 0) {
      const deltaY = velocity.y * deltaTime;
      const nextY = playerY + deltaY;

      const isCollidingY = this.doesCollide(playerX, nextY);

      if (!isCollidingY) {
        playerY = nextY;
      } else {
        // Find the closest valid position by binary search
        playerY = this.findClosestValidPosition(playerY, nextY, playerX, "y");
        velocity.y = 0;
      }
    }

    if (playerX !== player.position.x || playerY !== player.position.y) {
      player.setPosition(playerX, playerY);
    }
  }

  /**
   * Use binary search to find the closest valid position to a target
   */
  private findClosestValidPosition(
    start: number,
    target: number,
    otherAxisPos: number,
    axis: "x" | "y",
  ): number {
    let low = start;
    let high = target;
    const epsilon = 0.001; // Very small value for precision

    // Ensure low < high
    if (low > high) {
      [low, high] = [high, low];
    }

    let bestValid = start;

    // Binary search for the closest valid position
    for (let i = 0; i < 20; i++) {
      // Limit iterations to prevent infinite loops
      const mid = (low + high) / 2;

      let testPos: Vector2;
      if (axis === "x") {
        testPos = { x: mid, y: otherAxisPos };
      } else {
        testPos = { x: otherAxisPos, y: mid };
      }

      // Use a more precise collision detection method
      const isColliding = this.doesCollide(testPos.x, testPos.y);

      if (!isColliding) {
        bestValid = mid;
        if (Math.abs(target - start) > 0) {
          // Move towards target
          if (target > start) {
            low = mid + epsilon;
          } else {
            high = mid - epsilon;
          }
        }
      } else {
        // Move away from target
        if (target > start) {
          high = mid - epsilon;
        } else {
          low = mid + epsilon;
        }
      }

      if (Math.abs(high - low) < epsilon) {
        break;
      }
    }

    return bestValid;
  }

  /**
   * Unified collision detection that checks if a position collides with solid voxels
   */
  private doesCollide(centerX: number, centerY: number): boolean {
    const width2 = this.player.boxSize.width * 0.5;
    const height2 = this.player.boxSize.height * 0.5;

    return isIteratorNonEmpty(
      GameEngine.voxelTerrain.getVoxelsInRect(
        { x: centerX - width2, y: centerY - height2 },
        { x: centerX + width2, y: centerY + height2 },
        true, // withSolids=true means only include solid voxels
      ),
    );
  }

  /**
   * Try to step up onto a voxel that's exactly 1 voxel higher
   */
  private tryStepUp(
    currentX: number,
    currentY: number,
    targetX: number,
  ): number | null {
    const { autoStepUpHeight } = this;

    // Calculate the step-up height (exactly 1 voxel)
    if (autoStepUpHeight === 0) {
      return null;
    }

    // Test if we can move to the target X position at the stepped-up Y position
    const targetY = currentY + autoStepUpHeight;

    const isOnGround = this.doesCollide(currentX, currentY - 0.01);
    if (!isOnGround) {
      return null;
    }

    // Check if the stepped-up position is valid (no collisions)
    const isCollidingAtStepUp = this.doesCollide(targetX, targetY);

    if (!isCollidingAtStepUp) {
      return targetY;
    }

    return null;
  }

  /**
   * Clean up resources
   */
  public dispose(): void {}
}
